<template>
  <div >

    <div class="header">
      <div class="left" @click="decrementDate">
        <i class="bi bi-chevron-left"></i>
      </div>
      <div class="week" @click="toggleCalendar">{{ formattedDayOfWeek }}</div>
      <div class="day" @click="toggleCalendar">{{ formattedDate }}</div>
      <div class="right" @click="incrementDate">
        <i class="bi bi-chevron-right"></i>
      </div>
    </div>
    <div v-if="loading" class="animate-pulse">
      <div
        class="relative mb-3 mx-2 p-3 bg-white text-gray-600 rounded-lg shadow-sm pb-7 clickable block">
        <div class="tab_num absolute left-4 top-3 text-blue-500 font-normal text-3xl"></div>
        <div class="time text-sm absolute right-2 bottom-1 text-blue-500 font-semibold"></div>
        <div class="ml-8">
          <div class="object text-lg font-bold"></div>
          <div class="tutor text-gray-700"></div>
          <div class="tutor text-gray-700"></div>
          <div class="tutor text-gray-700"></div>
          <div class="room_num absolute right-2 top-2 text-gray-700 text-sm"></div>
        </div>
      </div>
    </div>
    <div v-if="showCalendar" class="schedule">
      <div class="schedule_header">
        <div class="left_schedule" @click="decrementMonth">
          <i class="bi bi-chevron-left"></i>
        </div>
        <div class="calendar" @click="resetDate">
          <i class="bi bi-calendar-date"></i>
        </div>
        <div class="month">{{ currentMonth }}</div>
        <div class="year">{{ currentYear }}</div>
        <div class="right_schedule" @click="incrementMonth">
          <i class="bi bi-chevron-right"></i>
        </div>
      </div>
      <table>
        <!-- The table rows here will be dynamically generated based on your Vue data and methods. -->
        <tr v-for="week in weeks" :key="week[0]">
          <th v-for="day in week" :key="day" :class="{'dayoff': isWeekend(day), 'today': isPickedDay(day)}">
            <span v-if="day">{{ day }}</span>
          </th>
        </tr>
      </table>
    </div>

    <!-- Lessons Part -->
    <div v-else>
      <div v-for="(lesson, index) in lessons" :key="lesson.tab_num" class="tab">
        <div class="tab_num">{{ index + 1 }}</div>
        <div class="time">{{ lesson.time }}</div>
        <div class="tab_obj">
          <div class="object">{{ lesson.object }}</div>
          <div class="tutor">{{ lesson.tutor }}</div>
          <div class="tutor">{{ lesson.building }}</div>
          <div class="tutor">{{ lesson.classroom }}</div>
          <div class="room_num">{{ lesson.code }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosClient from "@/axiosBot"; // assuming you have this set up already
import dayjs from "dayjs";
import 'dayjs/locale/ru';
import 'dayjs/locale/kk';
import 'dayjs/locale/en';

import { useRoute } from 'vue-router';
let localeData = require('dayjs/plugin/localeData')
dayjs.extend(localeData); // extend dayjs to use the localeData plugin
import {capitalize} from "vue";

export default {
  name: 'LessonSchedule',
  data() {
    return {
      lessons: [],
      chosen_date: '',
      lang: '',
      showCalendar: false,
      loading: false,
      student_id: ''
    };
  },
  computed: {
    formattedDate() {
      return dayjs(this.chosen_date).locale('ru').format('DD.MM.YYYY');
    },
    formattedDayOfWeek() {
      return capitalize(dayjs(this.chosen_date).locale('ru').format('dddd'));
    },
    currentMonth() {
      return capitalize(dayjs(this.chosen_date).locale('ru').format('MMMM'));
    },
    currentYear() {
      return dayjs(this.chosen_date).year();
    },
    daysInMonth() {
      return dayjs(this.chosen_date).daysInMonth();
    },
    weeks() {
      let days = [];
      const startDay = dayjs(this.chosen_date).startOf('month').day() || 7; // 7 for Sunday since dayjs treats Sunday as 0 and we want Monday (1) to be the first day.
      const daysInMonth = this.daysInMonth;

      // add empty days at the beginning of the month
      for (let i = 1; i < startDay; i++) {
        days.push(null);
      }

      // add actual days of the month
      for (let i = 1; i <= daysInMonth; i++) {
        days.push(i);
      }

      const weeks = [];
      while (days.length) {
        weeks.push(days.splice(0, 7));
      }

      // if the last week isn't full, fill it with null values
      while (weeks[weeks.length - 1].length < 7) {
        weeks[weeks.length - 1].push(null);
      }

      return weeks;
    },

    startDay() {
      let start = dayjs(this.chosen_date).startOf('month').day();
      // Adjust Sunday (which dayjs regards as 0) to be 6 (end of the week)
      if (start === 0) start = 6;
      else start -= 1;
      return start;
    }

  },
  methods: {
    async fetchData() {
      this.lessons = [];
      this.loading = true;  // Set loading to true when starting to fetch
      try {
        const response = await axiosClient.get(`/schedule_student_list?date=${this.chosen_date}&lang=${this.lang}&student_id=${this.student_id}`);
        this.lessons = response.data;
      } catch (error) {
        console.error("Error fetching lessons:", error);
      } finally {
        this.loading = false;  // Set loading to false when done fetching
      }
    },
    isWeekend(day) {
      if (day == null)
        return false;
      // Check if the given day in the current month is a weekend
      const date = dayjs(this.chosen_date).date(day);
      const dayOfWeek = date.day();
      return dayOfWeek === 0 || dayOfWeek === 6;  // 0 is Sunday and 6 is Saturday
    },
    decrementDate() {
      this.chosen_date = dayjs(this.chosen_date).subtract(1, 'day').format('YYYY-MM-DD');
      this.fetchData();
      this.showCalendar = false;
    },
    incrementDate() {
      this.chosen_date = dayjs(this.chosen_date).add(1, 'day').format('YYYY-MM-DD');
      this.fetchData();
      this.showCalendar = false;
    },
    decrementMonth() {
      this.chosen_date = dayjs(this.chosen_date).subtract(1, 'month').format('YYYY-MM-DD');
      this.fetchData();
    },
    incrementMonth() {
      this.chosen_date = dayjs(this.chosen_date).add(1, 'month').format('YYYY-MM-DD');
      this.fetchData();
    },
    navigateToStudentsList() {
      // this.$router.push({ name: 'students_list', query: {room_id: room_id, date: this.chosen_date, student_id: this.student_id} });
    },
    isToday(day) {
      return day === dayjs().date() && dayjs(this.chosen_date).isSame(dayjs(), 'month');
    },
    isPickedDay(day) {
      return day === dayjs(this.chosen_date).date() && dayjs(this.chosen_date).isSame(dayjs(), 'month');
    },
    resetDate() {
      this.chosen_date = dayjs().format('YYYY-MM-DD');
      this.fetchData();
    },
    changeDate(day) {
      this.chosen_date = dayjs(this.chosen_date).date(day).format('YYYY-MM-DD');
      this.showCalendar = false;  // hide the calendar
      this.fetchData();
    },
    toggleCalendar() {
      this.showCalendar = !this.showCalendar;
    }
  },
  created() {

    let tailwindJIT = document.createElement('script');
    tailwindJIT.setAttribute('src', 'https://cdn.tailwindcss.com');
    tailwindJIT.setAttribute('id', 'tailwind-jit-cdn');
    document.body.appendChild(tailwindJIT);
    let tailwindLink = document.createElement('link');
    tailwindLink.setAttribute('rel', 'stylesheet');
    tailwindLink.setAttribute('href', 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css');
    tailwindLink.setAttribute('id', 'tailwindcss-cdn');
    document.head.appendChild(tailwindLink);

  },
  mounted() {
    const route = useRoute();
    this.lang = route.params.locale;
    this.chosen_date = new URL(window.location.href).searchParams.get('date');
    this.student_id = new URL(window.location.href).searchParams.get('student_id');

    this.fetchData();
    dayjs.locale(this.lang);
  }

}
</script>


<style scoped>
/* http://meyerweb.com/eric/tools/css/reset/
   v2.0 | 20110126
   License: none (public domain)
*/

html, body, div, span, applet, object, iframe,
h1, h2, h3, h4, h5, h6, p, blockquote, pre,
a, abbr, acronym, address, big, cite, code,
del, dfn, em, img, ins, kbd, q, s, samp,
small, strike, strong, sub, sup, tt, var,
b, u, i, center,
dl, dt, dd, ol, ul, li,
fieldset, form, label, legend,
table, caption, tbody, tfoot, thead, tr, th, td,
article, aside, canvas, details, embed,
figure, figcaption, footer, header, hgroup,
menu, nav, output, ruby, section, summary,
time, mark, audio, video {
  margin: 0;
  padding: 0;
  border: 0;
  font-size: 100%;
  font: inherit;
  vertical-align: baseline;
}
/* HTML5 display-role reset for older browsers */
article, aside, details, figcaption, figure,
footer, header, hgroup, menu, nav, section {
  display: block;
}
body {
  line-height: 1;
}
ol, ul {
  list-style: none;
}
blockquote, q {
  quotes: none;
}
blockquote:before, blockquote:after,
q:before, q:after {
  content: '';
  content: none;
}
table {
  border-collapse: collapse;
  border-spacing: 0;
}

/* main style
*/

@import url('https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap');


body {
  background: #f0f1f3;
  font-family: 'Roboto', sans-serif;
}
.header {
  background: #fff;
  padding: 20px 10px;
  color: #1891fc;
  text-align: center;
  line-height: 22px;
  position: relative;
}
.left {
  position: absolute;
  left: 30px;
  top: 30px;
  font-size: 24px;
}
.right {
  position: absolute;
  right: 30px;
  top: 30px;
  font-size: 24px;
}
.week {
  font-size: 20px;
  font-weight: 600;
}
.day {
  color: #233647;
}
.schedule {
  margin-top: 15px;
  margin-bottom: 10px;
  margin-left: 10px;
  margin-right: 10px;
  padding: 10px;
  background: #fff;
  color: #000;
  border-radius: 5px;
  line-height: 22px;
}
.schedule_header {
  color: #1891fc;
  line-height: 22px;
  position: relative;
  margin-bottom: 15px;
  text-align: right;
  padding-right: 80px;
}
.left_schedule {
  position: absolute;
  left: 10px;
  top: 10px;
  font-size: 24px;
}
.right_schedule {
  position: absolute;
  right: 10px;
  top: 10px;
  font-size: 24px;
}
.calendar {
  position: absolute;
  left: 75px;
  top: 10px;
  font-size: 24px;
}
.month {
  font-size: 20px;
  font-weight: 400;
  color: #1891fc;
}
.year {
  color: #c9cacc;
  padding-right: 30px;
  color: #000;
}
table {
  width: 100%;
  border: 1px solid #bdbdbd;
}
tr {
  border: 1px solid #bdbdbd;
}
th {
  border: 1px solid #bdbdbd;
  padding-bottom: 50px;
  padding-right: 5px;
  padding-top: 5px;
  text-align: right;
}
.dayoff {
  background: #e3f0fc;
  color: #64676c;
}
.today {
  background: #93cdff;
}
th span {
  background: #ff5050;
  color: #fff;
  padding: 5px;
  margin-right: -5px;
  border-radius: 50%;
}
</style>
